import { ChevronDown } from "@/components/bnp-icons";
import { useEffect, useState, useRef } from "react";
import { safeMoment } from "@/utils/safeMoment";

import Image from "next/image";
import { useQuery } from "@tanstack/react-query";
import { PropertyService } from "@/services";
import { capitalizeFirstLetter } from "@/utils/capitalizeFirstLetter";
import parseHttpError from "@/utils/parseHttpError";
import { NoSubscriptions } from "@/components/bnp-illustrations";
import { Pagination } from "@/components/bnp-ui";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import DeletePropertyModal from "../DeletePropertyModal";
import UserPropertyEditForm from "@/components/pages/user/dashboard/properties/UserPropertyEditForm";
import { Modal } from "@mantine/core";
import { toast } from "react-toastify";
import { useDisclosure } from "@mantine/hooks";
import PropertyInsightModal from "@/components/pages/user/dashboard/properties/Tabs/listing-tabs/PropertyInsightsModal";
import { IconEye } from "@tabler/icons-react";
import { PropertyStatus } from "@/types";

const SortIndicator = ({
  active,
  direction,
}: {
  active: boolean;
  direction: "asc" | "desc" | null;
}) => {
  return (
    <svg
      width="10"
      height="14"
      viewBox="0 0 10 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="ml-1"
    >
      <path
        d="M9.16732 4.91667L5.00065 0.75L0.833984 4.91667H9.16732Z"
        fill={active && direction === "asc" ? "#2D96A7" : "#868C98"}
      />
      <path
        d="M9.16732 9.08333L5.00065 13.25L0.833984 9.08333H9.16732Z"
        fill={active && direction === "desc" ? "#2D96A7" : "#868C98"}
      />
    </svg>
  );
};

const getStatusStyle = (status: any) => {
  switch (status.toLowerCase()) {
    case "approved":
      return "bg-green-success text-main-900";
    case "pending":
      return "bg-[#FEF3EB] text-[#CC7914]";
    case "rejected":
      return "bg-[#F8C9D2] text-[#8C1823]";
    case "rented":
      return "bg-[#E7EBEF] text-[#1C7B44]";
    case "available":
      return "bg-[#CBF5E5] text-[#1C7B44]";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const StatusDropdown = ({ listing, onStatusChange }: any) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const statusOptions = [
    "Available",
    ...(listing?.listing_type === "rent" || listing?.listing_type === "shortlet"
      ? ["Rented"]
      : []),
  ];

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      //@ts-ignore
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleStatusSelect = (status: any) => {
    onStatusChange(listing.id, status.toLowerCase());
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between font-medium p-2 gap-1 w-[150px] h-[40px] text-sm border border-soft-200 rounded-lg text-main-900 hover:text-bnp-cyan cursor-pointer"
      >
        <span>Change status</span>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-[150px] bg-white border border-soft-200 rounded-lg shadow-lg">
          {statusOptions.map((status) => (
            <div
              key={status}
              className="px-4 py-2 hover:bg-weak-100 cursor-pointer text-main-900 text-sm"
              onClick={() => handleStatusSelect(status)}
            >
              {status}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ListingsTable = ({ listingType }: any) => {
  const router = useRouter();
  const [
    propertyInsightOpened,
    { open: openPropertyInsight, close: closePropertyInsight },
  ] = useDisclosure(false);

  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(
    null,
  );

  const handleOpenPropertyInsight = (id: string) => {
    setSelectedPropertyId(id);
    openPropertyInsight();
  };

  const handleClosePropertyInsight = () => {
    setSelectedPropertyId(null);
    closePropertyInsight();
  };

  const [sortField, setSortField] = useState<string | null>(null);
  const [properties, setProperties] = useState<any>([]);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const pageSize = 8;
  const { page } = router.query;
  const currentPage = page ? Number(page) : 1;

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState<any>(null);

  const handleEditClick = (listing: any) => {
    setEditingProperty(listing);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingProperty(null);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Apply sorting to properties
  const getSortedProperties = () => {
    if (!sortField) return properties;

    return [...properties].sort((a, b) => {
      let valA, valB;

      if (sortField === "date") {
        valA = new Date(a.created_at).getTime();
        valB = new Date(b.created_at).getTime();
      } else if (sortField === "listing") {
        valA = a.description.toLowerCase();
        valB = b.description.toLowerCase();
      } else {
        return 0;
      }

      if (sortDirection === "asc") {
        return valA > valB ? 1 : -1;
      } else {
        return valA < valB ? 1 : -1;
      }
    });
  };

  const handleStatusChange = async (propertyId: string, newStatus: string) => {
    try {
      // Update status locally first for immediate UI feedback
      setProperties(
        properties.map((property: any) =>
          property.id === propertyId
            ? { ...property, status: newStatus }
            : property,
        ),
      );

      await PropertyService.UpdatePropertyStatus({
        id: propertyId,
        status: newStatus as PropertyStatus,
      });

      propertiesRefetch();
    } catch (error) {
      console.error("Failed to update status:", error);
      // Revert to original status if API call fails
      propertiesRefetch();
    }
  };

  const handleDeleteClick = (listing: any) => {
    setSelectedProperty(listing);
    setDeleteError(null);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async (reason: string) => {
    if (!selectedProperty) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await PropertyService.DeleteProperty(selectedProperty.id, reason);
      toast.success("Property deleted successfully");
      setDeleteModalOpen(false);
      setSelectedProperty(null);
      propertiesRefetch();
    } catch (error: any) {
      setDeleteError(parseHttpError(error).message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const getPropertiesRequest = async () => {
    try {
      const response = await PropertyService.GetUserProperties({
        listingType,
        pageNumber: currentPage,
        pageSize,
      });
      return response?.data;
    } catch (error: any) {
      console.log(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const {
    data: propertiesData,
    isLoading: propertiesIsLoading,
    isError: propertiesIsError,
    refetch: propertiesRefetch,
    isSuccess: propertiesIsSuccess,
  } = useQuery({
    queryKey: ["userproperties", listingType, page],
    queryFn: getPropertiesRequest,
    staleTime: 0,
  });

  useEffect(() => {
    if (propertiesData) {
      setProperties(propertiesData?.data);
    }
  }, [propertiesData]);

  useEffect(() => {
    // Re-sort properties whenever sortField or sortDirection changes
    // This isn't strictly necessary since getSortedProperties() is called in the render,
    // but it keeps the sorted data in state which can be useful for other operations
    if (sortField) {
      setProperties(getSortedProperties());
    }
  }, [sortField, sortDirection]);

  const handlePageChange = (pageNumber: number) => {
    const newQuery = {
      ...router.query,
      page: pageNumber.toString(),
    };

    router.push({
      pathname: router.pathname,
      query: newQuery,
    });
  };

  // Get sorted properties for rendering
  const sortedProperties = getSortedProperties();
  console.log("sorted properties", sortedProperties);

  return (
    <div className="w-[100%] lg:w-[950px]">
      <div className="">
        <table className="w-[100%] lg:w-[950px] border-collapse">
          <thead className="rounded-[24px] w-[100%] max-w-[100%]">
            <tr className="text-grey-7 bg-weak-100 rounded-[24px]">
              <th
                className="p-4 text-left cursor-pointer"
                onClick={() => handleSort("date")}
              >
                <span className="inline-flex items-center font-normal">
                  Date
                  <SortIndicator
                    active={sortField === "date"}
                    direction={sortField === "date" ? sortDirection : null}
                  />
                </span>
              </th>
              <th
                className="p-4 text-left cursor-pointer"
                onClick={() => handleSort("listing")}
              >
                <span className="inline-flex items-center font-normal">
                  Listing
                  <SortIndicator
                    active={sortField === "listing"}
                    direction={sortField === "listing" ? sortDirection : null}
                  />
                </span>
              </th>
              <th
                className="p-4 text-left cursor-pointer"
                onClick={() => handleSort("status")}
              >
                <span className="inline-flex items-center font-normal">
                  Status
                </span>
              </th>
              <th className="p-4 text-left">
                <span className="inline-flex items-center font-normal">
                  Actions
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {propertiesIsLoading ? (
              <tr>
                <td colSpan={4}>
                  <div className="flex flex-col justify-center items-center w-full h-fit p-8">
                    <div className="w-fit h-fit">
                      <Image
                        src={"/assets/images/property-default.svg"}
                        width={200}
                        height={200}
                        alt={"property"}
                        className="w-[400px] h-[400px] object-cover animate-pulse"
                      />
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              <>
                {sortedProperties?.length > 0 ? (
                  <>
                    {sortedProperties.map((listing: any, index: any) => {
                      const mediaUrl =
                        listing?.image_url?.length > 0
                          ? listing.image_url[0]
                          : null;

                      const isVideo =
                        mediaUrl && /\.(mp4|webm|ogg|mov|m4v)$/i.test(mediaUrl);
                      return (
                        <tr
                          key={index}
                          className="border-b cursor-pointer border-soft-200 bg-white hover:bg-grey-6 transition-colors ease-linear delay-150"
                        >
                          <td className="p-4">
                            <div className="text-sm text-grey-7">
                              {safeMoment(listing?.created_at)?.format(
                                "DD MMMM, YYYY",
                              )}
                            </div>
                          </td>

                          <td className="p-4">
                            <div className="flex items-center gap-4">
                              <div className="w-16 h-12 overflow-hidden rounded-md bg-gray-100">
                                {!mediaUrl ? (
                                  <img
                                    src="/assets/images/property-default.svg"
                                    alt="property"
                                    className="w-full h-full object-cover"
                                  />
                                ) : isVideo ? (
                                  <video
                                    src={mediaUrl}
                                    muted
                                    playsInline
                                    preload="metadata"
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <img
                                    src={mediaUrl}
                                    alt="property"
                                    className="w-full h-full object-cover"
                                  />
                                )}
                              </div>
                              <div
                                onClick={() => {
                                  router.push(`/property/${listing?.id}`);
                                }}
                              >
                                <h3 className="text-main-900 hover:text-bnp-cyan text-base min-w-[274px] max-w-[274px] truncate font-bold">
                                  {listing?.description}
                                </h3>
                                <div className="flex items-center gap-1">
                                  <span className="text-sm text-grey-7 truncate">
                                    {listing?.address}
                                  </span>
                                </div>
                                <div className="flex flex-col gap-1 text-grey-7 text-sm mt-1">
                                  <div className="flex items-center gap-1">
                                    <span className="truncate">
                                      Listed By :{" "}
                                      {`${listing?.realtor?.first_name} ${listing?.realtor?.last_name}`}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1.5 text-sm text-grey-7 opacity-90 transition-opacity hover:opacity-100">
                                  <IconEye
                                    size={16}
                                    stroke={1.5}
                                    className="shrink-0"
                                  />
                                  <span className="truncate">
                                    {listing?.impressions_count ?? 0} Views
                                  </span>
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className="p-4">
                            <div className="flex items-center gap-6">
                              <span
                                className={`flex gap-1 items-center px-3 py-1 min-w-[105px] rounded-lg text-sm ${getStatusStyle(
                                  listing?.status,
                                )}`}
                              >
                                <svg
                                  width="5"
                                  height="4"
                                  viewBox="0 0 5 4"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <circle
                                    cx="2.21094"
                                    cy="2"
                                    r="2"
                                    fill="currentColor"
                                  />
                                </svg>
                                {capitalizeFirstLetter(listing?.status)}
                              </span>

                              <StatusDropdown
                                listing={listing}
                                onStatusChange={handleStatusChange}
                              />
                            </div>
                          </td>

                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() =>
                                  handleOpenPropertyInsight(listing.id)
                                }
                              >
                                Insights
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditClick(listing);
                                }}
                                className="flex items-center gap-1 px-3 py-2 text-sm text-bnp-cyan hover:bg-cyan-50 rounded-lg transition-colors"
                                title="Edit property"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M12 20h9" />
                                  <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                                </svg>
                                Edit
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(listing);
                                }}
                                className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete property"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                  <line x1="10" y1="11" x2="10" y2="17" />
                                  <line x1="14" y1="11" x2="14" y2="17" />
                                </svg>
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </>
                ) : (
                  <tr>
                    <td
                      colSpan={4}
                      className="self-center mt-16 ml-auto mr-auto w-full justify-center items-center flex flex-col"
                    >
                      <NoSubscriptions />
                      <p className="text-grey-7 lg:w-[408px] text-center mt-4 text-lg">
                        No properties found
                      </p>
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>

      {properties?.length > 0 && (
        <Pagination
          metadata={propertiesData?.metadata}
          pageSize={pageSize}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          className="w-full mt-8"
        />
      )}

      <DeletePropertyModal
        opened={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedProperty(null);
          setDeleteError(null);
        }}
        onConfirm={handleDeleteConfirm}
        propertyId={selectedProperty?.id || ""}
        propertyAddress={selectedProperty?.address}
        loading={deleteLoading}
        error={deleteError}
      />
      <PropertyInsightModal
        opened={propertyInsightOpened}
        onClose={handleClosePropertyInsight}
        propertyId={selectedPropertyId ?? ""}
      />
      <Modal
        opened={editModalOpen}
        onClose={closeEditModal}
        centered
        size="848px"
        radius="lg"
        title={
          <h2 className="text-blue-primary font-semibold text-lg">
            Edit property
          </h2>
        }
      >
        {editingProperty && (
          <UserPropertyEditForm
            property={editingProperty}
            onSuccess={() => {
              closeEditModal();
              propertiesRefetch();
            }}
            onCancel={closeEditModal}
          />
        )}
      </Modal>
    </div>
  );
};

export default ListingsTable;
